// React Admin 4.x uses string constants instead of imported constants

import { apiHeaders } from '@src/services/api/helpers';
import mapResource from '@src/utils/map-resource';

import { generateBodyParams } from './generateBodyParams';

import {
    getGetListURL,
    getGetOneURL,
    getGetManyURL,
    getCreateURL,
    getUpdateURL,
    getUpdateManyURL,
    getDeleteURL,
    getDeleteManyURL,
} from './requestURL';
import { processValidationError } from './process-validation-error';
import { processResponseData } from './processResponseData';

// Helper function to execute request
const executeRequest = (requestGenerator, type, resource) => {
    let TotalCountFromHeader;
    return requestGenerator
        .then(({ url, options }) => fetch(url, options))
        .then((res) => {
            TotalCountFromHeader = res.headers.get('Total');
            TotalCountFromHeader &&= parseInt(TotalCountFromHeader, 10);

            if (res.status === 401) {
                // React Router v7: Use window.location for navigation in data provider
                // since we're outside of React component context
                window.location.href = '/unauthorized';
                const error = new Error('Authorization error');
                error.status = 401;
                throw error;
            }

            if (res.status === 422) {
                return processValidationError(res);
            }

            if (res.status >= 500) {
                throw new Error('500 Internal Server Error. Please check unique identifiers (ie. \'slug\') is not already in use.');
            }

            // We need to renew jwt token every api request
            const newJwt = res.headers.get('jwt');
            if (newJwt) {
                window.localStorage.setItem('jwt', newJwt);
            }

            return res.json();
        })
        .then((response) => (
            processResponseData(type, resource, response, TotalCountFromHeader)
        ));
};

// React Admin 4.x data provider object
const dataProvider = {
    getList(rawResource, params) {
        const resource = mapResource(rawResource);
        const options = {
            headers: apiHeaders(),
        };

        const requestGenerator = new Promise((resolve) => {
            resolve({ url: getGetListURL(resource, rawResource, params), options });
        });

        return executeRequest(requestGenerator, 'getList', resource);
    },

    getOne(rawResource, params) {
        const resource = mapResource(rawResource);
        const options = {
            headers: apiHeaders(),
        };

        const requestGenerator = new Promise((resolve) => {
            resolve({ url: getGetOneURL(resource, params), options });
        });

        return executeRequest(requestGenerator, 'getOne', resource);
    },

    getMany(rawResource, params) {
        const resource = mapResource(rawResource);
        const options = {
            headers: apiHeaders(),
        };

        const requestGenerator = new Promise((resolve) => {
            resolve({ url: getGetManyURL(resource, params), options });
        });

        return executeRequest(requestGenerator, 'getMany', resource);
    },

    create(rawResource, params) {
        const resource = mapResource(rawResource);
        const options = {
            headers: apiHeaders(),
            method: 'POST',
        };

        const requestGenerator = generateBodyParams(resource, params.data, options.method)
            .then((bodyParam) => {
                options.body = JSON.stringify(bodyParam);
                return { url: getCreateURL(resource), options };
            });

        return executeRequest(requestGenerator, 'create', resource);
    },

    update(rawResource, params) {
        const resource = mapResource(rawResource);
        const options = {
            headers: apiHeaders(),
            method: 'PUT',
        };

        const requestGenerator = generateBodyParams(resource, params.data, options.method)
            .then((bodyParam) => {
                options.body = JSON.stringify(bodyParam);
                return { url: getUpdateURL(resource, params), options };
            });

        return executeRequest(requestGenerator, 'update', resource);
    },

    updateMany(rawResource, params) {
        const resource = mapResource(rawResource);
        const options = {
            headers: apiHeaders(),
            method: 'PUT',
        };

        const requestGenerator = new Promise((resolve) => {
            resolve({ url: getUpdateManyURL(resource, params), options });
        });

        return executeRequest(requestGenerator, 'updateMany', resource);
    },

    delete(rawResource, params) {
        const resource = mapResource(rawResource);
        const options = {
            headers: apiHeaders(),
            method: 'DELETE',
        };

        const requestGenerator = new Promise((resolve) => {
            resolve({ url: getDeleteURL(resource, params), options });
        });

        return executeRequest(requestGenerator, 'delete', resource);
    },

    deleteMany(rawResource, params) {
        const resource = mapResource(rawResource);
        const options = {
            headers: apiHeaders(),
            method: 'DELETE',
        };

        const requestGenerator = new Promise((resolve) => {
            resolve({ url: getDeleteManyURL(resource, params), options });
        });

        return executeRequest(requestGenerator, 'deleteMany', resource);
    },
};

export default dataProvider;
